import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import AppLayout from "@/components/AppLayout";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import SignaturePad from "@/components/SignaturePad";
import SquareCardForm from "@/components/SquareCardForm";
import { TIDS } from "@/constants/testIds";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { CheckCircle2, ArrowRight, ArrowLeft } from "lucide-react";

const STEPS = [
  { key: "select", label: "Camper" },
  { key: "form", label: "Details" },
  { key: "review", label: "Review" },
  { key: "sign", label: "Sign" },
  { key: "pay", label: "Payment" },
  { key: "done", label: "Confirm" },
];

function Stepper({ current }) {
  const idx = STEPS.findIndex((s) => s.key === current);
  return (
    <div className="flex items-center gap-2 mb-6" aria-label="Progress">
      {STEPS.map((s, i) => (
        <div key={s.key} className="flex-1">
          <div className={`h-1.5 rounded-full ${i <= idx ? "bg-[#2F50A1]" : "bg-[#E5E7EB]"}`} />
          <div className={`mt-1 text-[10px] uppercase tracking-widest ${i === idx ? "text-[#2F50A1] font-semibold" : "text-[#9CA3AF]"}`}>
            {s.label}
          </div>
        </div>
      ))}
    </div>
  );
}

export default function RegisterFlow() {
  const { tripKey } = useParams();
  const navigate = useNavigate();

  const [trip, setTrip] = useState(null);
  const [campers, setCampers] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [searching, setSearching] = useState(false);
  const [seasonId, setSeasonId] = useState("");
  const [config, setConfig] = useState(null);
  const [step, setStep] = useState("select");

  // Form state
  const [selectedCamper, setSelectedCamper] = useState(null);
  const [parentName, setParentName] = useState("");
  const [parentEmail, setParentEmail] = useState("");
  const [parentEmailCc, setParentEmailCc] = useState("");
  const [grade, setGrade] = useState("");
  const [reg, setReg] = useState(null);
  const [signature, setSignature] = useState({ type: "typed", typedValue: "" });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [t, cfg] = await Promise.all([
          api.get(`/trips/${tripKey}`),
          api.get(`/config`),
        ]);
        setTrip(t.data);
        setConfig(cfg.data);
      } catch (e) {
        toast.error(formatApiError(e));
      }
    })();
  }, [tripKey]);

  // Debounced camper search: only queries when parent types 2+ characters.
  useEffect(() => {
    const q = searchTerm.trim();
    if (q.length < 2) {
      setCampers([]);
      setSeasonId("");
      return;
    }
    setSearching(true);
    const handle = setTimeout(async () => {
      try {
        const { data } = await api.get(`/campers/eligible`, { params: { q } });
        setCampers(data.campers || []);
        setSeasonId(data.season_id || "");
      } catch (e) {
        toast.error(formatApiError(e));
      } finally {
        setSearching(false);
      }
    }, 250);
    return () => clearTimeout(handle);
  }, [searchTerm]);

  useEffect(() => {
    if (selectedCamper) {
      const grd = selectedCamper.grade_entering || "";
      setGrade(grd);
      if (selectedCamper.parent_email) setParentEmail(selectedCamper.parent_email);
      if (selectedCamper.parent_name) setParentName(selectedCamper.parent_name);
    }
  }, [selectedCamper]);

  const canGoForm = !!selectedCamper;
  const ccOk = !parentEmailCc.trim() || /.+@.+\..+/.test(parentEmailCc.trim());
  const canReview = parentName.trim().length > 1 && /.+@.+\..+/.test(parentEmail) && grade.trim().length > 0 && ccOk;

  const goStep = (target) => {
    window.scrollTo({ top: 0, behavior: "smooth" });
    setStep(target);
  };

  const startRegistration = async () => {
    setSubmitting(true);
    try {
      const camperName = `${selectedCamper.first_name} ${selectedCamper.last_name}`.trim();
      const { data } = await api.post(`/registrations/start`, {
        trip_key: tripKey,
        camper_id: selectedCamper.id,
        camper_name: camperName,
        camper_grade: grade,
        parent_print_name: parentName,
        parent_email: parentEmail,
        parent_email_cc: parentEmailCc.trim() || null,
      });
      setReg(data);
      goStep("sign");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSubmitting(false);
    }
  };

  const submitSignature = async () => {
    if (signature.type === "typed" && !(signature.typedValue || "").trim()) {
      toast.error("Please type your full name to sign.");
      return;
    }
    if (signature.type === "drawn" && !signature.drawnDataUrl) {
      toast.error("Please draw your signature to sign.");
      return;
    }
    setSubmitting(true);
    try {
      const payload =
        signature.type === "typed"
          ? { signature_type: "typed", typed_value: signature.typedValue }
          : { signature_type: "drawn", drawn_data_url: signature.drawnDataUrl };
      await api.post(`/registrations/${reg.id}/sign`, payload);
      toast.success("Permission slip signed. A confirmation email is on the way.");
      goStep("pay");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSubmitting(false);
    }
  };

  const chooseOfficePayment = async () => {
    setSubmitting(true);
    try {
      await api.post(`/registrations/${reg.id}/pay/office`);
      goStep("done");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSubmitting(false);
    }
  };

  const payByCard = async (sourceId) => {
    try {
      const { data } = await api.post(`/registrations/${reg.id}/pay/card`, { sourceId });
      toast.success(`Payment ${data.payment_status || "processed"}. Placed on ${data.status} list.`);
      goStep("done");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  if (!trip) {
    return (
      <AppLayout>
        <div className="rrdc-card p-6 text-[#4B5563]">Loading.</div>
      </AppLayout>
    );
  }
  if (!trip.is_open) {
    return (
      <AppLayout>
        <div className="rrdc-card p-6 border-red-200 bg-red-50">
          <div className="font-semibold text-red-800">Sign ups are closed for this trip.</div>
          <Link to="/" className="text-[#2F50A1] underline text-sm mt-2 inline-block">Back to trips</Link>
        </div>
      </AppLayout>
    );
  }

  const detailLine = trip.key === "broadway"
    ? (trip.detail_show_name ? `Broadway show: ${trip.detail_show_name}` : "Broadway show details coming soon.")
    : ([trip.detail_city, trip.detail_state].filter(Boolean).join(", ")
        ? `Destination: ${[trip.detail_city, trip.detail_state].filter(Boolean).join(", ")}`
        : "Overnight destination coming soon.");

  return (
    <AppLayout>
      <div className="mb-2 text-xs uppercase tracking-widest text-[#2F50A1] font-semibold">{trip.display_name}</div>
      <h1 className="rrdc-heading text-2xl font-bold text-[#111827] mb-1">Register your camper</h1>
      <p className="text-sm text-[#4B5563] mb-4 no-hyphens">{detailLine}</p>

      <Stepper current={step} />

      <AnimatePresence mode="wait">
        <motion.div
          key={step}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -10 }}
          transition={{ duration: 0.2 }}
        >
          {step === "select" && (
            <div className="rrdc-card p-5">
              <div className="text-xs uppercase tracking-widest text-[#6B7280] font-semibold mb-2">
                Find your camper. Navigators and CITs only.
              </div>
              <Label htmlFor="camper-search" className="text-sm text-[#4B5563]">Start typing your child's name</Label>
              <Input
                id="camper-search"
                autoFocus
                value={searchTerm}
                onChange={(e) => { setSearchTerm(e.target.value); setSelectedCamper(null); }}
                placeholder="First or last name"
                className="h-12 mt-1"
                data-testid="camper-search"
                autoComplete="off"
              />
              <p className="text-xs text-[#6B7280] mt-2 no-hyphens">
                Type at least 2 letters and matches will appear.
              </p>

              <div className="mt-4 space-y-2" data-testid={TIDS.camperSelect}>
                {searchTerm.trim().length < 2 ? null : searching ? (
                  <div className="text-sm text-[#6B7280]">Searching.</div>
                ) : campers.length === 0 ? (
                  <div className="text-sm text-[#4B5563] p-3 border rounded-xl bg-[#FFFBEB] border-[#FDE68A] no-hyphens">
                    No eligible camper matches that name. Only campers in Navigator or CIT divisions for the current season can sign up. Please check the spelling or contact the main office.
                  </div>
                ) : (
                  campers.map((c) => {
                    const active = selectedCamper?.id === c.id;
                    return (
                      <button
                        key={c.id}
                        type="button"
                        onClick={() => setSelectedCamper(c)}
                        className={`w-full text-left p-3 rounded-xl border transition-colors ${
                          active ? "border-[#2F50A1] bg-[#EEF2FF]" : "border-[#E5E7EB] bg-white hover:bg-[#F9FAFB]"
                        }`}
                        data-testid={TIDS.camperItem}
                      >
                        <div className="font-semibold text-[#111827]">{c.first_name} {c.last_name}</div>
                        <div className="text-xs text-[#4B5563]">{c.division}{c.grade_entering ? `, entering ${c.grade_entering}` : ""}</div>
                      </button>
                    );
                  })
                )}
              </div>

              <Button
                onClick={() => goStep("form")}
                disabled={!canGoForm}
                className="mt-5 w-full h-12 rrdc-btn-primary rounded-xl"
                data-testid="camper-continue"
              >
                Continue <ArrowRight className="ml-1" size={18} />
              </Button>
              {seasonId ? <div className="mt-3 text-[10px] uppercase tracking-widest text-[#9CA3AF]">Season {seasonId}</div> : null}
            </div>
          )}

          {step === "form" && (
            <div className="rrdc-card p-5 space-y-4">
              {trip.permission_body ? (
                <div className="rounded-xl p-4 bg-[#F9FAFB] border border-[#E5E7EB] text-sm text-[#111827] no-hyphens whitespace-pre-line leading-relaxed">
                  {trip.permission_body}
                </div>
              ) : null}

              <div>
                <div className="text-xs uppercase tracking-widest text-[#6B7280] font-semibold">Camper Name</div>
                <div className="mt-1 font-semibold text-[#111827]">
                  {selectedCamper.first_name} {selectedCamper.last_name}
                </div>
                <div className="text-xs text-[#4B5563]">{selectedCamper.division}</div>
              </div>
              <div>
                <Label htmlFor="grade">Camper Grade Entering in Sept &lsquo;26</Label>
                <Input
                  id="grade"
                  data-testid={TIDS.camperGrade}
                  value={grade}
                  onChange={(e) => setGrade(e.target.value)}
                  className="h-12 mt-1"
                  placeholder="e.g. 8th"
                />
              </div>
              <div>
                <Label htmlFor="parent-name">Parent/Guardian Print Name</Label>
                <Input
                  id="parent-name"
                  data-testid={TIDS.parentPrintName}
                  value={parentName}
                  onChange={(e) => setParentName(e.target.value)}
                  className="h-12 mt-1"
                  placeholder="Full name"
                />
              </div>

              <div>
                <Label htmlFor="parent-email">Parent email for confirmation</Label>
                <Input
                  id="parent-email"
                  type="email"
                  data-testid={TIDS.parentEmail}
                  value={parentEmail}
                  onChange={(e) => setParentEmail(e.target.value)}
                  className="h-12 mt-1"
                  placeholder="you@example.com"
                />
                {selectedCamper.parent_email ? (
                  <p className="text-xs text-[#6B7280] mt-1">
                    Prefilled from the camp office. You can change it here for this signup.
                  </p>
                ) : (
                  <p className="text-xs text-[#6B7280] mt-1">
                    We do not have an email on file for this camper. This is used to send your confirmation only.
                  </p>
                )}
              </div>

              <div>
                <Label htmlFor="parent-email-cc">Add another email (optional)</Label>
                <Input
                  id="parent-email-cc"
                  type="email"
                  data-testid="parent-email-cc"
                  value={parentEmailCc}
                  onChange={(e) => setParentEmailCc(e.target.value)}
                  className="h-12 mt-1"
                  placeholder="second parent or guardian"
                />
                <p className="text-xs text-[#6B7280] mt-1">
                  A copy of the signed permission slip and PDF will be sent here too.
                </p>
              </div>

              <div className="rounded-xl p-3 bg-[#F3F4F6] text-sm text-[#4B5563] no-hyphens">
                <div className="text-xs uppercase tracking-widest text-[#6B7280] font-semibold mb-1">
                  Permission statement
                </div>
                {trip.permission_statement}
              </div>

              <div className="flex gap-2">
                <Button
                  variant="outline"
                  onClick={() => goStep("select")}
                  className="flex-1 h-12 rounded-xl"
                >
                  <ArrowLeft className="mr-1" size={18} /> Back
                </Button>
                <Button
                  onClick={() => goStep("review")}
                  disabled={!canReview}
                  className="flex-1 h-12 rrdc-btn-primary rounded-xl"
                  data-testid={TIDS.submitReview}
                >
                  Review
                </Button>
              </div>
            </div>
          )}

          {step === "review" && (
            <div className="rrdc-card p-5 space-y-4">
              <div className="text-xs uppercase tracking-widest text-[#6B7280] font-semibold">Review before signing</div>
              <div>
                <div className="text-xs text-[#6B7280]">Trip</div>
                <div className="font-semibold text-[#111827]">{trip.display_name}</div>
                <div className="text-sm text-[#4B5563]">{detailLine}</div>
              </div>
              <ReviewRow label="Camper Name" value={`${selectedCamper.first_name} ${selectedCamper.last_name}`} />
              <ReviewRow label="Camper Grade Entering in Sept &lsquo;26" value={grade} />
              <ReviewRow label="Parent/Guardian Print Name" value={parentName} />
              <ReviewRow label="Confirmation email" value={parentEmail} />
              {parentEmailCc.trim() ? (
                <ReviewRow label="Also copy to" value={parentEmailCc.trim()} />
              ) : null}
              <div className="rounded-xl p-3 bg-[#F3F4F6] text-sm text-[#4B5563] no-hyphens">
                <div className="text-xs uppercase tracking-widest text-[#6B7280] font-semibold mb-1">Permission statement</div>
                {trip.permission_statement}
              </div>
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => goStep("form")} data-testid={TIDS.reviewBack} className="flex-1 h-12 rounded-xl">
                  <ArrowLeft className="mr-1" size={18} /> Edit
                </Button>
                <Button
                  onClick={startRegistration}
                  disabled={submitting}
                  data-testid={TIDS.reviewProceed}
                  className="flex-1 h-12 rrdc-btn-primary rounded-xl"
                >
                  {submitting ? "Continuing" : "Continue to sign"}
                </Button>
              </div>
            </div>
          )}

          {step === "sign" && (
            <div className="space-y-4">
              {trip.permission_body ? (
                <div className="rrdc-card p-4 max-h-80 overflow-y-auto text-sm text-[#111827] whitespace-pre-line leading-relaxed no-hyphens border border-[#E5E7EB]">
                  {trip.permission_body}
                </div>
              ) : null}
              <div className="rounded-xl p-3 bg-[#F3F4F6] text-sm text-[#111827] no-hyphens">
                <div className="text-xs uppercase tracking-widest text-[#6B7280] font-semibold mb-1">Permission statement</div>
                {trip.permission_statement}
              </div>
              <div className="rrdc-card p-4 text-sm text-[#4B5563] no-hyphens">
                By signing below you agree to the full permission slip and statement above. Your signature is timestamped in US Eastern time and a copy will be emailed to you.
              </div>
              <SignaturePad
                initialTypedValue={parentName}
                onChange={setSignature}
              />
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => goStep("review")} className="flex-1 h-12 rounded-xl">
                  <ArrowLeft className="mr-1" size={18} /> Back
                </Button>
                <Button
                  onClick={submitSignature}
                  disabled={submitting}
                  data-testid={TIDS.signSubmit}
                  className="flex-1 h-12 rrdc-btn-primary rounded-xl"
                >
                  {submitting ? "Signing" : "Sign and continue"}
                </Button>
              </div>
            </div>
          )}

          {step === "pay" && (
            <PayStep
              trip={trip}
              reg={reg}
              config={config}
              onPayOffice={chooseOfficePayment}
              onPayCard={payByCard}
              submitting={submitting}
            />
          )}

          {step === "done" && (
            <div className="rrdc-card p-6 text-center" data-testid={TIDS.doneMessage}>
              <div className="mx-auto w-14 h-14 rounded-full bg-[#D1FAE5] flex items-center justify-center text-[#065F46] mb-3">
                <CheckCircle2 size={28} />
              </div>
              <div className="rrdc-heading text-xl font-bold">You are all set.</div>
              <p className="mt-2 text-[#4B5563] no-hyphens">
                A confirmation email is on the way to <span className="font-semibold">{parentEmail}</span>. If you selected pay main office, please bring your cash or check to the main office between 9:00 AM and 4:00 PM Eastern Time to complete the registration.
              </p>
              <Link to="/" className="mt-6 inline-block underline text-[#2F50A1]">Back to trips</Link>
            </div>
          )}
        </motion.div>
      </AnimatePresence>
    </AppLayout>
  );
}

function ReviewRow({ label, value }) {
  return (
    <div>
      <div className="text-xs text-[#6B7280]">{label}</div>
      <div className="font-medium text-[#111827]">{value}</div>
    </div>
  );
}

function PayStep({ trip, reg, config, onPayOffice, onPayCard, submitting }) {
  const [choice, setChoice] = useState(null);
  const price = trip.price_cents || 0;
  const squareOk = config?.square?.configured && price > 0;
  return (
    <div className="space-y-4">
      <div className="rrdc-card p-5">
        <div className="rrdc-heading text-lg font-bold">Choose how you would like to pay</div>
        <p className="text-sm text-[#4B5563] mt-1 no-hyphens">
          {price > 0 ? `Trip price: $${(price / 100).toFixed(2)}` : "Please contact the office for the current price."}
        </p>
        <div className="mt-4 grid gap-3">
          <button
            type="button"
            className={`text-left p-4 rounded-xl border ${choice === "card" ? "border-[#2F50A1] bg-[#EEF2FF]" : "border-[#E5E7EB] bg-white hover:bg-[#F9FAFB]"} ${!squareOk ? "opacity-60 cursor-not-allowed" : ""}`}
            onClick={() => squareOk && setChoice("card")}
            disabled={!squareOk}
            data-testid={TIDS.payCard}
          >
            <div className="font-semibold">Pay now by card</div>
            <div className="text-xs text-[#4B5563] mt-1 no-hyphens">
              Secure card payment. On success, your camper is placed on the active list, subject to capacity.
            </div>
            {!squareOk ? (
              <div className="text-xs text-[#B91C1C] mt-1">Card payments are not enabled yet.</div>
            ) : null}
          </button>

          <button
            type="button"
            className={`text-left p-4 rounded-xl border ${choice === "office" ? "border-[#2F50A1] bg-[#EEF2FF]" : "border-[#E5E7EB] bg-white hover:bg-[#F9FAFB]"}`}
            onClick={() => setChoice("office")}
            data-testid={TIDS.payOffice}
          >
            <div className="font-semibold">Pay the main office by cash or check</div>
            <div className="text-xs text-[#4B5563] mt-1 no-hyphens">
              Paying by cash or check does not guarantee a spot until payment is received. Spots fill up fast, so make your payment as soon as possible.
            </div>
            <div className="text-xs text-[#2F50A1] mt-2 font-semibold">
              Office hours: 9:00 AM to 4:00 PM Eastern Time for cash and check payments.
            </div>
          </button>
        </div>
      </div>

      {choice === "card" ? (
        <SquareCardForm config={config?.square} amountCents={price} onToken={onPayCard} disabled={submitting} />
      ) : null}

      {choice === "office" ? (
        <div className="rrdc-card p-5">
          <div className="text-sm text-[#4B5563] no-hyphens">
            When you continue, we will hold your registration as awaiting payment. Please drop off your cash or check at the main office as soon as possible.
          </div>
          <div className="text-sm text-[#111827] mt-2 font-semibold">
            Main office is open 9:00 AM to 4:00 PM Eastern Time for cash and check payments.
          </div>
          <Button
            onClick={onPayOffice}
            disabled={submitting}
            className="mt-3 w-full h-12 rrdc-btn-gold rounded-xl font-semibold"
          >
            {submitting ? "Submitting" : "Confirm pay main office"}
          </Button>
        </div>
      ) : null}
    </div>
  );
}
